/**
 * Shared billing rules for the OnArk system.
 */

/**
 * Picks the plan to nudge a church toward when it's near/over its check-in pool.
 * Predictability-first (NOT lowest cost): the smallest larger plan whose pool
 * covers current usage — so the bill goes flat with no overage. If none is big
 * enough, the largest plan; if already on the biggest, none.
 *
 * Generic over the plan shape: it reads only `.pool` and returns the matched
 * plan as-is, so each caller keeps its own PlanPool type (the API billing page's
 * `{ id, title, pool }` and the worker sweep's `{ title, pool }` both fit). This
 * is the single source of truth so the billing page (getSubscriptionDetail) and
 * the usage-warning email (runUsageWarningSweep) always recommend the same plan.
 */
export function pickUpgrade<T extends { pool: number }>(
  plans: T[],
  currentPool: number,
  used: number
): T | null {
  // Unlimited plans participate as pool = Infinity (always larger, always
  // covering) — callers map a poolless metered price to Infinity, never 0.
  const larger = plans.filter(p => p.pool > currentPool);

  if (larger.length === 0) return null;

  const covering = larger
    .filter(p => p.pool >= used)
    .sort((a, b) => a.pool - b.pool);
  const largest = [...larger].sort((a, b) => b.pool - a.pool)[0];

  return covering[0] ?? largest;
}

/**
 * Monthly included pool for a tier = round(annual / 12). Keeps "Switch to
 * Monthly" / plan-picker monthly cards on the same annual capacity (resetting
 * each month) instead of the old 50 / 150 / 500·mo grandfather pools.
 */
export function monthlyIncludedFromAnnual(perYear: number): number {
  return Math.round(perYear / 12);
}

export interface MeteredCheckinTier {
  unit_amount: number | null;
  // Stripe SDKs type this as string or Decimal depending on version — we only
  // ever Number() it, so stay structurally permissive.
  unit_amount_decimal?: unknown;
  up_to: number | null;
}

/**
 * Reads a metered check-in Price's graduated tiers into a pool + overage rate.
 * The one tier parser for the billing page (getSubscriptionDetail), the
 * usage-warning sweep, and upgrade recommendations — so "what does this price
 * include" can't drift between surfaces.
 *
 * - `{ pool: number, overageUnitCents }` — capped plan: free tier, then overage.
 * - `{ pool: null, overageUnitCents: 0 }` — unlimited: a single all-free tier
 *   (the current-ladder Scale shape). Callers hide usage meters / skip warnings
 *   on `pool: null`, and treat it as an infinite pool when ranking upgrades.
 * - `null` — not a pool-shaped price (untiered, or a paid unbounded first tier
 *   = per-use billing from the first check-in). Never invent a pool of 0.
 */
export function parseCheckinPriceTiers(
  tiers: MeteredCheckinTier[]
): { pool: number | null; overageUnitCents: number } | null {
  const first = tiers[0];

  if (!first) return null;

  if (first.up_to == null) {
    const free =
      (first.unit_amount ?? Number(first.unit_amount_decimal ?? 0)) === 0;

    return free && tiers.length === 1
      ? { overageUnitCents: 0, pool: null }
      : null;
  }

  const overageTier = tiers[1];
  const decimal = overageTier?.unit_amount_decimal;
  const overageUnitCents =
    overageTier?.unit_amount ??
    (decimal != null ? Math.round(Number(decimal)) : 0);

  return { overageUnitCents, pool: first.up_to };
}

/**
 * Length of the public card-on-trial signup, in days. THE one home for it.
 *
 * This is the number Stripe charges on (`trial_period_days` in the
 * `finalizeTrial` subscription create), the number the card step counts down
 * to, and the number every "free 21-day trial / cancel before day 21" promise
 * on the marketing site is making. They were three independent literals before
 * — the Stripe call used a bare inline `21` — so a policy change could move the
 * copy without moving the charge date, or vice versa. Import this instead.
 *
 * Not to be confused with the retired Welcome Aboard purchase, which ran a
 * **30**-day trial paired with a 30-day launch challenge; any 30s left in old
 * data or copy are that product, not this constant drifting. Likewise the
 * worker's retro-trial `walled: 21` is its own sweep mechanism, not a Stripe
 * trial.
 */
export const TRIAL_DAYS = 21;

/**
 * Plan contract (funnel v2).
 *
 * New signups default annual. Included check-ins on YEARLY are per *year*;
 * MONTHLY counterparts use {@link monthlyIncludedFromAnnual} so interval
 * switch stays capacity-aligned. Trial + paid subs include a metered check-in
 * Price (graduated free tier = included, then overage). A null pool means
 * unlimited: the metered Price is a single all-free tier that tracks usage
 * but never bills (current-ladder Scale — grandfathered pre-catalog Scale
 * subs keep their own metered prices and are not touched by this contract).
 *
 * Persist `includedCheckInsPerYear` / `includedCheckInsPerMonth` on
 * `Pricing.stripePricing` so deploy verify + ensure-pricing share one source
 * of truth with the seed.
 */
export const ANNUAL_PLAN_CONTRACT = [
  {
    annualBaseCents: 39_000,
    attendanceLabel: 'Up to ~500 attendance',
    checkinsLabel: '100 check-ins / year',
    displayPrice: '$390/yr',
    includedCheckInsPerMonth: 8, // round(100/12)
    includedCheckInsPerYear: 100,
    monthlyBaseCents: 3_900,
    monthlyDisplayPrice: '$39/mo',
    /**
     * Per-check-in overage after the free tier (cents). $1.50 on Starter and
     * Growth (Rachel, 2026-09-18) so the upgrade pays for itself: Starter
     * breaks even with Growth at Growth's 500 pool, Growth with Scale at
     * 1,500. The old $0.60/$0.40 made staying put cheaper until ~1,100/yr.
     * Existing subs keep the archived $0.60/$0.40 prices.
     */
    overageRateCents: 150,
    title: 'Starter',
  },
  {
    annualBaseCents: 99_000,
    attendanceLabel: 'Up to ~1,500 attendance',
    checkinsLabel: '500 check-ins / year',
    displayPrice: '$990/yr',
    includedCheckInsPerMonth: 42, // round(500/12)
    includedCheckInsPerYear: 500,
    monthlyBaseCents: 9_900,
    monthlyDisplayPrice: '$99/mo',
    overageRateCents: 150,
    title: 'Growth',
  },
  {
    annualBaseCents: 249_000,
    attendanceLabel: '1,500+ attendance / multisite',
    checkinsLabel: 'Unlimited check-ins',
    displayPrice: '$2,490/yr',
    // null = unlimited (Rachel, 2026-08-25): the current-ladder Scale plan
    // never meters check-ins — its Stripe price is a single all-free tier.
    // Grandfathered pre-catalog Scale subs keep their own metered prices.
    includedCheckInsPerMonth: null,
    includedCheckInsPerYear: null,
    monthlyBaseCents: 24_900,
    monthlyDisplayPrice: '$249/mo',
    overageRateCents: 0,
    title: 'Scale',
  },
] as const;

/**
 * `onark_included` metadata value for an unlimited (uncapped) metered price —
 * a number can't express "no pool", so the catalog stamps this literal.
 */
export const UNLIMITED_CHECKINS_METADATA = 'unlimited';

/** `onark_included` metadata for a plan's pool (null = unlimited). */
export function checkinsIncludedMetadata(included: number | null): string {
  return included === null ? UNLIMITED_CHECKINS_METADATA : String(included);
}

/**
 * `onark_overage_cents` metadata stamped on capped check-in Prices, so a rate
 * change mints a new Price instead of reusing the old one (Stripe list
 * results carry no tiers without an expand). Unlimited prices never bill, so
 * their rate is not part of the match.
 */
function matchesCheckinPool(
  metadata: Record<string, string> | null | undefined,
  included: number | null,
  overageRateCents: number
): boolean {
  return (
    metadata?.onark_included === checkinsIncludedMetadata(included) &&
    (included === null ||
      metadata?.onark_overage_cents === String(overageRateCents))
  );
}

/** Graduated tiers for a metered check-in Price: the pool free, then the
 * overage rate. A null pool is one all-free tier (tracks usage, never bills). */
export function checkinPriceTiers(
  included: number | null,
  overageRateCents: number
): Array<{ unit_amount: number; up_to: number | 'inf' }> {
  if (included === null) return [{ unit_amount: 0, up_to: 'inf' }];

  return [
    { unit_amount: 0, up_to: included },
    { unit_amount: overageRateCents, up_to: 'inf' },
  ];
}

/** Catalog metadata for a metered check-in Price — the fields the
 * `isCatalog*CheckinPrice` matchers read back. */
export function checkinPriceMetadata(
  tier: string,
  interval: 'year' | 'month',
  included: number | null,
  overageRateCents: number
): Record<string, string> {
  return {
    onark_catalog: ANNUAL_PRICING_CATALOG,
    onark_included: checkinsIncludedMetadata(included),
    onark_interval: interval,
    ...(included === null
      ? {}
      : { onark_overage_cents: String(overageRateCents) }),
    onark_role: 'checkin',
    onark_tier: tier,
  };
}

export type AnnualPlanTitle = (typeof ANNUAL_PLAN_CONTRACT)[number]['title'];

/**
 * Monthly-equivalent *anchor* for an annual plan — `annualBaseCents / 12`.
 * Starter → "$32.50/mo", Growth → "$82.50/mo", Scale → "$207.50/mo".
 *
 * ⚠️ This is NOT `monthlyDisplayPrice` ("$39/mo"), which is the real
 * month-to-month plan and bills twelve times a year. This number is never
 * charged to anyone: an annual plan takes `annualBaseCents` in ONE payment.
 * It exists so a church can compare us against per-month tools, and it must
 * always be rendered next to the annual total and the words "billed annually"
 * — alone it reads as a monthly subscription we don't sell.
 *
 * Derived, never hand-written: a hard-coded "$32.50" would silently rot the
 * next time a plan price moves, which is exactly the drift `displayPrice` and
 * the marketing PLANS list were consolidated to prevent.
 *
 * Takes the one field it reads rather than a whole contract entry — same shape
 * as {@link pickUpgrade} above. `ANNUAL_PLAN_CONTRACT` is `as const`, so a
 * narrower signature would type its literal cent amounts into the parameter and
 * refuse any other price.
 */
export function monthlyEquivalentDisplay(plan: {
  annualBaseCents: number;
}): string {
  const dollars = plan.annualBaseCents / 12 / 100;

  return `$${dollars.toLocaleString('en-US', {
    // Whole dollars stay clean ("$40/mo"); anything else shows real cents
    // rather than a rounded number that wouldn't multiply back to the annual.
    maximumFractionDigits: 2,
    minimumFractionDigits: Number.isInteger(dollars) ? 0 : 2,
  })}/mo`;
}

/**
 * Stripe metadata catalog id for annual base + metered check-in Prices.
 * Deploy `ensure-pricing` finds-or-creates by this key (idempotent — never
 * creates a second Product/Price for the same catalog+tier+role). Bump ONLY
 * when the Price shape or included pools intentionally change (creates a new
 * generation once; old Prices are archived).
 */
export const ANNUAL_PRICING_CATALOG = 'annual_checkin_v2';

/** @deprecated use ANNUAL_PRICING_CATALOG */
export const ANNUAL_SOFT_CAP_CATALOG = ANNUAL_PRICING_CATALOG;

/** Stripe object with the catalog metadata we stamp on Products/Prices. */
export type CatalogStripeMetadata = {
  metadata?: Record<string, string> | null;
};

export function isCatalogProduct(
  product: CatalogStripeMetadata & { active?: boolean },
  tier: string
): boolean {
  return (
    product.active !== false &&
    product.metadata?.onark_catalog === ANNUAL_PRICING_CATALOG &&
    product.metadata?.onark_tier === tier
  );
}

type CatalogBasePrice = CatalogStripeMetadata & {
  active?: boolean;
  billing_scheme?: string | null;
  recurring?: { interval?: string | null } | null;
  unit_amount?: number | null;
};

/** Shared by the annual/monthly base matchers — only the interval differs. */
function isCatalogBasePrice(
  price: CatalogBasePrice,
  tier: string,
  interval: 'year' | 'month',
  unitAmount: number
): boolean {
  return (
    price.active !== false &&
    price.metadata?.onark_catalog === ANNUAL_PRICING_CATALOG &&
    price.metadata?.onark_tier === tier &&
    price.metadata?.onark_role === 'base' &&
    price.recurring?.interval === interval &&
    price.unit_amount === unitAmount &&
    price.billing_scheme !== 'tiered'
  );
}

export function isCatalogAnnualBasePrice(
  price: CatalogBasePrice,
  tier: string,
  unitAmount: number
): boolean {
  return isCatalogBasePrice(price, tier, 'year', unitAmount);
}

type CatalogCheckinPrice = CatalogStripeMetadata & {
  active?: boolean;
  recurring?: {
    interval?: string | null;
    usage_type?: string | null;
  } | null;
};

/** Shared by the annual/monthly checkin matchers below — only the billing
 * interval differs between them. */
function isCatalogCheckinPrice(
  price: CatalogCheckinPrice,
  tier: string,
  interval: 'year' | 'month',
  included: number | null,
  overageRateCents: number
): boolean {
  return (
    price.active !== false &&
    price.metadata?.onark_catalog === ANNUAL_PRICING_CATALOG &&
    price.metadata?.onark_tier === tier &&
    price.metadata?.onark_role === 'checkin' &&
    price.recurring?.interval === interval &&
    price.recurring?.usage_type === 'metered' &&
    matchesCheckinPool(price.metadata, included, overageRateCents)
  );
}

export function isCatalogAnnualCheckinPrice(
  price: CatalogCheckinPrice,
  tier: string,
  includedCheckInsPerYear: number | null,
  overageRateCents: number
): boolean {
  return isCatalogCheckinPrice(
    price,
    tier,
    'year',
    includedCheckInsPerYear,
    overageRateCents
  );
}

export function isCatalogMonthlyBasePrice(
  price: CatalogBasePrice,
  tier: string,
  unitAmount: number
): boolean {
  return isCatalogBasePrice(price, tier, 'month', unitAmount);
}

export function isCatalogMonthlyCheckinPrice(
  price: CatalogCheckinPrice,
  tier: string,
  includedCheckInsPerMonth: number | null,
  overageRateCents: number
): boolean {
  return isCatalogCheckinPrice(
    price,
    tier,
    'month',
    includedCheckInsPerMonth,
    overageRateCents
  );
}

export function annualPlanBullets(
  plan: (typeof ANNUAL_PLAN_CONTRACT)[number]
): string[] {
  if (plan.includedCheckInsPerYear === null) {
    return ['Unlimited check-ins', 'All core features included'];
  }
  return [
    `${plan.includedCheckInsPerYear.toLocaleString('en-US')} check-ins per year`,
    'Additional check-ins billed per use',
    'All core features included',
  ];
}

export function monthlyPlanBullets(
  plan: (typeof ANNUAL_PLAN_CONTRACT)[number]
): string[] {
  if (plan.includedCheckInsPerMonth === null) {
    return ['Unlimited check-ins', 'All core features included'];
  }
  return [
    `${plan.includedCheckInsPerMonth.toLocaleString('en-US')} check-ins per month`,
    'Additional check-ins billed per use',
    'All core features included',
  ];
}

export type StripeMode = 'test' | 'live';

/**
 * Point a Pricing.stripePricing blob at base + metered check-in Prices for the
 * current Stripe mode. Returns ids that were replaced so deploy can archive
 * them (existing subscriptions keep working). `includedKey` is the one field
 * that differs between the annual and monthly catalogs.
 */
function applyCatalogPrices(
  stripePricing: unknown,
  mode: StripeMode,
  basePriceId: string,
  checkinPriceId: string,
  includedKey: 'includedCheckInsPerYear' | 'includedCheckInsPerMonth',
  included: number | null
): { next: Record<string, unknown>; toArchive: string[] } {
  const prev =
    stripePricing && typeof stripePricing === 'object'
      ? { ...(stripePricing as Record<string, unknown>) }
      : {};
  const baseKey = mode === 'live' ? 'liveBasePriceId' : 'testBasePriceId';
  const checkinKey = mode === 'live' ? 'liveCheckinId' : 'testCheckinId';
  const toArchive: string[] = [];
  const keep = new Set([basePriceId, checkinPriceId]);

  for (const key of [baseKey, checkinKey] as const) {
    const old = prev[key];
    if (
      typeof old === 'string' &&
      old &&
      !keep.has(old) &&
      old.startsWith('price_')
    ) {
      toArchive.push(old);
    }
  }

  return {
    next: {
      ...prev,
      [baseKey]: basePriceId,
      [checkinKey]: checkinPriceId,
      [includedKey]: included,
    },
    toArchive,
  };
}

/** Annual catalog variant of {@link applyCatalogPrices}. */
export function applyAnnualCatalogPrices(
  stripePricing: unknown,
  mode: StripeMode,
  basePriceId: string,
  checkinPriceId: string,
  includedCheckInsPerYear: number | null
): { next: Record<string, unknown>; toArchive: string[] } {
  return applyCatalogPrices(
    stripePricing,
    mode,
    basePriceId,
    checkinPriceId,
    'includedCheckInsPerYear',
    includedCheckInsPerYear
  );
}

/** Monthly catalog variant of {@link applyCatalogPrices}. */
export function applyMonthlyCatalogPrices(
  stripePricing: unknown,
  mode: StripeMode,
  basePriceId: string,
  checkinPriceId: string,
  includedCheckInsPerMonth: number | null
): { next: Record<string, unknown>; toArchive: string[] } {
  return applyCatalogPrices(
    stripePricing,
    mode,
    basePriceId,
    checkinPriceId,
    'includedCheckInsPerMonth',
    includedCheckInsPerMonth
  );
}

export type PricingRowForAudit = {
  available: boolean;
  displayPrice: string | null;
  interval: string;
  stripePricing: unknown;
  title: string | null;
};

function readIncludedNumber(
  stripePricing: unknown,
  key: 'includedCheckInsPerYear' | 'includedCheckInsPerMonth'
): { present: boolean; value: number | null } {
  if (!stripePricing || typeof stripePricing !== 'object') {
    return { present: false, value: null };
  }
  const sp = stripePricing as Record<string, unknown>;
  if (!(key in sp)) {
    return { present: false, value: null };
  }
  const raw = sp[key];
  if (raw === null) return { present: true, value: null };
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return { present: true, value: raw };
  }
  return { present: true, value: Number.NaN };
}

function readIncludedCheckInsPerYear(stripePricing: unknown): {
  present: boolean;
  value: number | null;
} {
  return readIncludedNumber(stripePricing, 'includedCheckInsPerYear');
}

function readIncludedCheckInsPerMonth(stripePricing: unknown): {
  present: boolean;
  value: number | null;
} {
  return readIncludedNumber(stripePricing, 'includedCheckInsPerMonth');
}

function hasBasePriceId(stripePricing: unknown): boolean {
  if (!stripePricing || typeof stripePricing !== 'object') return false;
  const sp = stripePricing as Record<string, unknown>;
  return Boolean(
    (typeof sp.testBasePriceId === 'string' && sp.testBasePriceId) ||
    (typeof sp.liveBasePriceId === 'string' && sp.liveBasePriceId)
  );
}

function hasCheckinPriceId(stripePricing: unknown): boolean {
  if (!stripePricing || typeof stripePricing !== 'object') return false;
  const sp = stripePricing as Record<string, unknown>;
  return Boolean(
    (typeof sp.testCheckinId === 'string' && sp.testCheckinId) ||
    (typeof sp.liveCheckinId === 'string' && sp.liveCheckinId)
  );
}

function auditIntervalRows(
  rows: PricingRowForAudit[],
  interval: 'YEARLY' | 'MONTHLY',
  displayPrice: (plan: (typeof ANNUAL_PLAN_CONTRACT)[number]) => string,
  includedKey: 'includedCheckInsPerYear' | 'includedCheckInsPerMonth',
  includedExpected: (
    plan: (typeof ANNUAL_PLAN_CONTRACT)[number]
  ) => number | null,
  readIncluded: (sp: unknown) => { present: boolean; value: number | null }
): string[] {
  const issues: string[] = [];

  for (const plan of ANNUAL_PLAN_CONTRACT) {
    const matches = rows.filter(
      r =>
        r.available &&
        r.interval === interval &&
        (r.title ?? '').toLowerCase() === plan.title.toLowerCase()
    );

    if (matches.length === 0) {
      issues.push(
        `Missing available ${interval} Pricing row for ${plan.title}`
      );
      continue;
    }
    if (matches.length > 1) {
      issues.push(
        `Multiple available ${interval} Pricing rows for ${plan.title} (${matches.length}) — keep one available`
      );
    }

    const row = matches[0]!;
    const expectedDisplay = displayPrice(plan);
    if (row.displayPrice !== expectedDisplay) {
      issues.push(
        `${plan.title} ${interval}: displayPrice expected "${expectedDisplay}", got "${row.displayPrice ?? ''}"`
      );
    }

    const expectedIncluded = includedExpected(plan);
    const included = readIncluded(row.stripePricing);
    if (!included.present) {
      issues.push(
        `${plan.title} ${interval}: stripePricing.${includedKey} missing (expected ${expectedIncluded})`
      );
    } else if (Number.isNaN(included.value as number)) {
      issues.push(
        `${plan.title} ${interval}: stripePricing.${includedKey} must be a number`
      );
    } else if (included.value !== expectedIncluded) {
      issues.push(
        `${plan.title} ${interval}: ${includedKey} expected ${expectedIncluded}, got ${included.value === null ? 'null' : included.value}`
      );
    }

    if (!hasBasePriceId(row.stripePricing)) {
      issues.push(
        `${plan.title} ${interval}: stripePricing needs testBasePriceId and/or liveBasePriceId`
      );
    }
    if (!hasCheckinPriceId(row.stripePricing)) {
      issues.push(
        `${plan.title} ${interval}: stripePricing needs testCheckinId and/or liveCheckinId (metered check-ins)`
      );
    }
  }

  return issues;
}

/**
 * Returns human-readable issues when available YEARLY Pricing rows do not
 * match {@link ANNUAL_PLAN_CONTRACT}. Empty array = yearly deploy-ready.
 */
export function auditAnnualPricingContract(
  rows: PricingRowForAudit[]
): string[] {
  return auditIntervalRows(
    rows,
    'YEARLY',
    p => p.displayPrice,
    'includedCheckInsPerYear',
    p => p.includedCheckInsPerYear,
    readIncludedCheckInsPerYear
  );
}

/**
 * Returns human-readable issues when available MONTHLY Pricing rows do not
 * match the monthly side of {@link ANNUAL_PLAN_CONTRACT} (annual÷12 pools).
 */
export function auditMonthlyPricingContract(
  rows: PricingRowForAudit[]
): string[] {
  return auditIntervalRows(
    rows,
    'MONTHLY',
    p => p.monthlyDisplayPrice,
    'includedCheckInsPerMonth',
    p => p.includedCheckInsPerMonth,
    readIncludedCheckInsPerMonth
  );
}

/** YEARLY + MONTHLY contract audit for deploy verify. */
export function auditPricingContract(rows: PricingRowForAudit[]): string[] {
  return [
    ...auditAnnualPricingContract(rows),
    ...auditMonthlyPricingContract(rows),
  ];
}

export interface AvailablePricingRowForDedupe {
  id: string;
  stripePricing?: unknown;
}

/**
 * Splits the currently-available Pricing rows of one (title, interval) into
 * the single row to keep and the duplicates to retire. Long-lived DBs (old
 * preview envs, pre-catalog seeds) can hold several available rows per tier —
 * e.g. a row still pointing at a Stripe price from another account — which
 * {@link auditPricingContract} rightly fails. The keeper is the first row
 * already on one of the catalog base prices, else the first row given (pass
 * rows ordered createdAt asc for the oldest). Retired rows keep their
 * stripePricing so grandfathered subscriptions still resolve.
 */
export function splitAvailablePricingDuplicates(
  rows: AvailablePricingRowForDedupe[],
  catalogBasePriceIds: string[]
): { keepId: string | null; retireIds: string[] } {
  if (rows.length === 0) return { keepId: null, retireIds: [] };

  const onCatalog = (row: AvailablePricingRowForDedupe): boolean => {
    if (!row.stripePricing || typeof row.stripePricing !== 'object') {
      return false;
    }
    const sp = row.stripePricing as Record<string, unknown>;
    return [sp.testBasePriceId, sp.liveBasePriceId].some(
      v => typeof v === 'string' && catalogBasePriceIds.includes(v)
    );
  };

  const keep = rows.find(onCatalog) ?? rows[0]!;

  return {
    keepId: keep.id,
    retireIds: rows.filter(r => r.id !== keep.id).map(r => r.id),
  };
}

/**
 * The "plan ended, keep building" wall (api accessRevokedPlugin). Lives here so
 * the admin UI can recognise the refusal and point at the fix instead of
 * failing silently (Help Scout 3473252716: a church could not create or delete
 * classes and saw nothing).
 *
 * BUILDING_BLOCKED_MESSAGE is for a church that HAS had a subscription (trial
 * ran out, plan cancelled). BUILDING_BLOCKED_NO_PLAN_MESSAGE is for one that
 * never had one, where "reactivate" is meaningless.
 */
export const BUILDING_BLOCKED_MESSAGE =
  'This church’s plan has ended. Reactivate your plan to keep building.';
export const BUILDING_BLOCKED_NO_PLAN_MESSAGE =
  'Your free access has ended. Pick a plan to keep building. Your classes are safe in the meantime.';

/**
 * The wall an error carries, with where to fix it, or null for any other error.
 * Matches by inclusion because graphql-request appends the response JSON to the
 * resolver's message.
 */
export function buildingBlocked(
  errorMessage: string
): { href: string; message: string } | null {
  if (errorMessage.includes(BUILDING_BLOCKED_NO_PLAN_MESSAGE)) {
    return {
      href: '/admin/billing/select-plan',
      message: BUILDING_BLOCKED_NO_PLAN_MESSAGE,
    };
  }
  if (errorMessage.includes(BUILDING_BLOCKED_MESSAGE)) {
    return { href: '/admin/billing', message: BUILDING_BLOCKED_MESSAGE };
  }

  return null;
}
